import { formatHeld, type HeldSymbol } from '@/lib/positions'
import type { Instrument, LiveQuote } from '@/lib/types'

/** Nombre de paires tenues dans la barre : au-delà, le défilement s'éternise. */
const MAX_SHOWN = 12

/**
 * Paires de la barre rapide : celles OÙ L'ON EST ENGAGÉ d'abord, puis les
 * favoris, puis les majeures.
 *
 * Les positions ouvertes passent devant parce qu'elles ne se choisissent pas :
 * elles existent. Les voir en tête évite d'en ouvrir une seconde sur une paire
 * déjà tenue, et évite surtout de la perdre de vue en naviguant.
 *
 * Exportée parce que l'écran doit s'abonner aux cotations de ces paires —
 * sans elles, la barre afficherait des prix vides.
 */
export function quickSymbols(
  instruments: Instrument[],
  favourites: string[],
  held: Record<string, HeldSymbol> = {},
): string[] {
  const majors = instruments.filter((item) => item.category === 'major').map((item) => item.name)
  // `dict.fromkeys` du pauvre : conserve l'ordre et supprime les doublons.
  return [...new Set([...Object.keys(held), ...favourites, ...majors])].slice(0, MAX_SHOWN)
}

/**
 * Barre de changement rapide de paire.
 *
 * Toujours visible, un seul appui pour changer d'instrument. Le sélecteur
 * complet (recherche, prix, onglets) reste accessible par le bouton de droite.
 *
 * Chaque paire porte SON PRIX. Un nom seul ne dit pas où en est le marché : on
 * ouvrait une paire pour la regarder, puis une autre, jusqu'à retrouver celle
 * qui bougeait. Le prix sous le nom répond à la question sans changer d'écran.
 *
 * L'étoile de droite met la paire affichée en favori sans ouvrir le sélecteur :
 * c'est là qu'on sait qu'une paire mérite d'être suivie — en la regardant.
 *
 * Ordre d'affichage : les favoris d'abord, puis les majeures. Une paire
 * indisponible sur le compte reste visible mais grisée — la voir disparaître
 * inquiéterait plus que de la voir barrée (§6).
 */
export function SymbolBar({
  instruments,
  favourites,
  current,
  quotes,
  held = {},
  onSelect,
  onToggleFavourite,
  onOpenPicker,
}: {
  instruments: Instrument[]
  favourites: string[]
  current: string
  quotes: Record<string, LiveQuote>
  /** Positions ouvertes par paire : voir `quickSymbols`. */
  held?: Record<string, HeldSymbol>
  onSelect: (symbol: string) => void
  onToggleFavourite: (symbol: string) => void
  onOpenPicker: () => void
}) {
  const shown = quickSymbols(instruments, favourites, held)
  const byName = new Map(instruments.map((item) => [item.name, item]))
  const starred = favourites.includes(current)

  return (
    // Sans cadre ni fond : la barre est imbriquée dans la barre d'outils
    // des graphiques, qui porte déjà la bordure.
    <div className="relative flex min-w-0 items-stretch gap-1">
      <div className="flex flex-1 gap-1 overflow-x-auto px-2 py-1.5 [scrollbar-width:none]">
        {shown.map((name) => {
          const instrument = byName.get(name)
          const active = name === current
          const disabled = instrument ? !instrument.enabled : false
          const quote = quotes[name]
          const price =
            quote && instrument ? Number(quote.mid).toFixed(instrument.digits) : null
          const open = held[name]

          return (
            <button
              key={name}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(name)}
              aria-current={active ? 'true' : undefined}
              title={
                open
                  ? `${open.count} position${open.count > 1 ? 's' : ''} ouverte${
                      open.count > 1 ? 's' : ''
                    } · ${formatHeld(open)}`
                  : undefined
              }
              className={`min-h-touch shrink-0 rounded px-2 py-1 text-center leading-tight ${
                active
                  ? 'bg-accent text-white'
                  : disabled
                    ? 'bg-elevated text-ink-muted/40 line-through'
                    : 'bg-elevated text-ink-muted hover:bg-border/60 active:bg-border'
              }`}
            >
              <span className="num block text-[12px] font-semibold">
                {/*
                  Pastille des positions ouvertes, teintée par le résultat
                  latent. Elle passe DEVANT l'étoile des favoris : être engagé
                  sur une paire pèse plus lourd que l'avoir mise de côté, et
                  deux marques côte à côte sur un bouton de 60 px ne se
                  distinguent plus.
                */}
                {open ? (
                  <span
                    aria-hidden
                    className={`mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle ${
                      active ? 'bg-white' : open.profit >= 0 ? 'bg-up' : 'bg-down'
                    }`}
                  />
                ) : (
                  favourites.includes(name) && (
                    <span className={active ? 'text-white/70' : 'text-warn'}>★ </span>
                  )
                )}
                {name}
              </span>
              {/* Le prix ne doit jamais changer la largeur du bouton d'une
                  décimale à l'autre : la barre se réorganiserait sous le doigt
                  au moment même où l'on vise une paire. D'où les chiffres
                  tabulaires de `num` et une ligne réservée, même vide. */}
              {/* Sur une paire tenue, le résultat latent remplace le prix :
                  c'est le chiffre qu'on cherche quand on y revient. */}
              <span
                className={`num block text-[10px] ${
                  open && !active
                    ? open.profit >= 0
                      ? 'text-up'
                      : 'text-down'
                    : active
                      ? 'text-white/80'
                      : 'text-ink-muted/70'
                }`}
              >
                {open ? formatHeld(open).replace(` ${open.currency}`, '') : (price ?? '—')}
              </span>
            </button>
          )
        })}
      </div>

      {/*
          Dégradé de bord : la barre défile, et rien ne le disait. Sur un
          téléphone, les paires au-delà de la quatrième sont hors de l'écran ;
          sans cet estompage on croit la liste terminée et on ouvre le
          sélecteur complet pour une paire qui était à un glissement de doigt.
          `pointer-events-none` : il ne doit rien intercepter.
      */}
      <div className="pointer-events-none absolute inset-y-0 right-[5.25rem] w-5 bg-gradient-to-r from-transparent to-panel" />

      <button
        type="button"
        onClick={() => onToggleFavourite(current)}
        aria-pressed={starred}
        aria-label={starred ? `Retirer ${current} des favoris` : `Mettre ${current} en favori`}
        title={starred ? 'Retirer des favoris' : 'Mettre en favori'}
        className={`flex min-h-touch w-9 shrink-0 items-center justify-center border-l border-border text-[15px] hover:bg-elevated/70 active:bg-elevated ${
          starred ? 'text-warn' : 'text-ink-muted/40'
        }`}
      >
        ★
      </button>

      <button
        type="button"
        onClick={onOpenPicker}
        aria-label="Toutes les paires"
        className="flex min-h-touch shrink-0 flex-col items-center justify-center border-l border-border px-2.5 leading-none text-ink-muted hover:bg-elevated/70 active:bg-elevated"
      >
        <span className="text-[16px]" aria-hidden>
          ⌕
        </span>
        {/* Le libellé, parce qu'une loupe seule ne dit pas ce qu'elle cherche :
            ici ce n'est pas une recherche dans la page, c'est la liste
            complète des instruments. */}
        <span className="mt-0.5 text-[9px] uppercase tracking-wide">Tout</span>
      </button>
    </div>
  )
}
